import React, { useState, useEffect } from 'react'
import { api } from '../utils/api'

interface DashboardPageProps {
  onImportContacts: () => void
  onNetworkAnalysis: () => void
  onSmartOutreach: () => void
  onCampaigns: () => void
  onVisualization?: () => void
  onGetStarted?: () => void
}

const DashboardPage: React.FC<DashboardPageProps> = ({ 
  onImportContacts, 
  onNetworkAnalysis, 
  onSmartOutreach,
  onCampaigns,
  onVisualization,
  onGetStarted
}) => {
  const [stats, setStats] = useState({
    totalContacts: 0,
    tier1Contacts: 0,
    recentUploads: 0,
    pendingOutreach: 0
  })
  const [recentContacts, setRecentContacts] = useState([])
  const [loading, setLoading] = useState(true)

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://network-crm-api.onrender.com'

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      // Use the API utility which handles HTTP-only cookies
      const contactsResponse = await api.get('/api/contacts?limit=500')

      if (contactsResponse.ok) {
        const data = await contactsResponse.json()
        const contacts = data.contacts || []
        const totalCount = data.pagination?.total || contacts.length
        
        setStats({
          totalContacts: totalCount,
          tier1Contacts: contacts.filter((c: any) => c.tier === 'TIER_1').length,
          recentUploads: contacts.filter((c: any) => {
            const uploadDate = new Date(c.createdAt)
            const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
            return uploadDate > weekAgo
          }).length,
          pendingOutreach: contacts.filter((c: any) => c.status === 'ACTIVE' || !c.status).length
        })
        
        // Get recent contacts (sorted by creation date)
        const sortedContacts = contacts.sort((a: any, b: any) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        setRecentContacts(sortedContacts.slice(0, 5))
      } else {
        // If API fails, try to show real data from local storage or use demo data
        console.warn('API call failed, using fallback data')
        throw new Error('API call failed')
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error)
      
      // Show empty state on error (no demo data)
      setStats({
        totalContacts: 0,
        tier1Contacts: 0,
        recentUploads: 0,
        pendingOutreach: 0
      })
      
      setRecentContacts([])
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm md:text-base text-gray-600">Overview of your network and activities</p>
      </div>

      {/* Onboarding Card - Show if user has no or very few contacts */}
      {stats.totalContacts < 5 && onGetStarted && (
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl flex items-center justify-center">
                <span className="text-white text-xl">🚀</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Maximize Your TrueCrew Experience
              </h3>
              <p className="text-gray-700 mb-4">
                Get the most out of TrueCrew by importing your LinkedIn connections, Google contacts, 
                and uploading your professional documents. Our guided setup takes just a few minutes.
              </p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={onGetStarted}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-600 text-white rounded-lg hover:from-orange-600 hover:to-amber-700 transition-colors font-medium"
                >
                  <span className="mr-2">✨</span> Get Started Guide
                </button>
                <span className="text-sm text-gray-500">~5 minutes</span>
              </div>
            </div>
            <button 
              className="text-gray-400 hover:text-gray-600 p-1"
              onClick={() => {/* Could add dismiss functionality later */}}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600">Total Contacts</p>
              <p className="text-xl md:text-3xl font-bold text-gray-900">{stats.totalContacts}</p>
            </div>
            <div className="h-8 w-8 md:h-12 md:w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg style={{width: '16px', height: '16px'}} className="md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600">Tier 1 Contacts</p>
              <p className="text-xl md:text-3xl font-bold text-green-600">{stats.tier1Contacts}</p>
            </div>
            <div className="h-8 w-8 md:h-12 md:w-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg style={{width: '16px', height: '16px'}} className="md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600">Recent Imports</p>
              <p className="text-xl md:text-3xl font-bold text-purple-600">{stats.recentUploads}</p>
            </div>
            <div className="h-8 w-8 md:h-12 md:w-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg style={{width: '16px', height: '16px'}} className="md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 md:p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-1">
              <p className="text-xs md:text-sm font-medium text-gray-600">Active Contacts</p>
              <p className="text-xl md:text-3xl font-bold text-orange-600">{stats.pendingOutreach}</p>
            </div>
            <div className="h-8 w-8 md:h-12 md:w-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg style={{width: '16px', height: '16px'}} className="md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Contacts */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Recent Contacts</h2>
              <p className="text-sm text-gray-500 mt-1">Latest additions to your network</p>
            </div>
            <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              View All
            </button>
          </div>
        </div>
        <div className="divide-y divide-gray-100">
          {recentContacts.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto h-12 w-12 text-gray-300 mb-4 flex items-center justify-center">
                <svg style={{width: '32px', height: '32px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">No contacts yet</p>
              <p className="text-gray-400 text-xs mt-1">Start by importing your first contacts!</p>
            </div>
          ) : (
            recentContacts.map((contact: any, index) => (
              <div key={index} className="px-4 py-4 md:px-6 hover:bg-gray-50 transition-colors cursor-pointer active:bg-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 md:space-x-4 flex-1 min-w-0">
                    <div className="h-10 w-10 md:h-12 md:w-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                      {contact.firstName[0]}{contact.lastName[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="text-sm md:text-base font-semibold text-gray-900 truncate">
                          {contact.firstName} {contact.lastName}
                        </p>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium flex-shrink-0 ${
                          contact.tier === 'TIER_1' ? 'bg-emerald-100 text-emerald-700 border border-emerald-200' :
                          contact.tier === 'TIER_2' ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                          'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}>
                          {contact.tier === 'TIER_1' ? 'T1' : 
                           contact.tier === 'TIER_2' ? 'T2' : 
                           'T3'}
                        </span>
                      </div>
                      <p className="text-xs md:text-sm text-blue-600 hover:text-blue-700 truncate">{contact.email}</p>
                      <div className="flex items-center text-xs md:text-sm text-gray-500 mt-1">
                        <svg style={{width: '14px', height: '14px'}} className="mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="font-medium text-gray-700 truncate">{contact.company}</span>
                        {contact.position && (
                          <>
                            <span className="mx-1 md:mx-2">•</span>
                            <span className="truncate">{contact.position}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <button className="p-2 md:p-3 hover:bg-gray-100 rounded-full transition-colors" title="Send Message">
                      <svg style={{width: '16px', height: '16px'}} className="text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </button>
                    <button className="p-2 md:p-3 hover:bg-gray-100 rounded-full transition-colors" title="View Profile">
                      <svg style={{width: '16px', height: '16px'}} className="text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Quick Actions</h2>
          <p className="text-sm text-gray-500 mt-1">Powerful tools to manage and analyze your network</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
            <div className="group cursor-pointer" onClick={onImportContacts}>
              <div className="p-4 md:p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 group-hover:shadow-md active:bg-blue-100">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Import Contacts</h3>
                  <p className="text-sm text-gray-600 mb-4">Drag & drop CSV files or connect to LinkedIn, CRM systems</p>
                  <div className="flex items-center justify-center text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-1 rounded-full">CSV • Excel • API</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer" onClick={onNetworkAnalysis}>
              <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-green-400 hover:bg-green-50 transition-all duration-200 group-hover:shadow-md">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
                    <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Network Analysis</h3>
                  <p className="text-sm text-gray-600 mb-4">Discover connections, identify influencers, find warm intros</p>
                  <div className="flex items-center justify-center text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-1 rounded-full">Powered by GPT-4</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer" onClick={onSmartOutreach}>
              <div className="p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all duration-200 group-hover:shadow-md">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                    <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Outreach</h3>
                  <p className="text-sm text-gray-600 mb-4">AI-generated personalized messages and campaigns</p>
                  <div className="flex items-center justify-center text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-1 rounded-full">Email • LinkedIn</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer" onClick={onCampaigns}>
              <div className="p-4 md:p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-all duration-200 group-hover:shadow-md active:bg-orange-100">
                <div className="text-center">
                  <div className="mx-auto h-12 w-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                    <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14-7H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V6a2 2 0 00-2-2zM8 15v4M16 15v4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaigns</h3>
                  <p className="text-sm text-gray-600 mb-4">Organize and track coordinated outreach efforts</p>
                  <div className="flex items-center justify-center text-xs text-gray-500">
                    <span className="bg-gray-100 px-2 py-1 rounded-full">Templates • Analytics</span>
                  </div>
                </div>
              </div>
            </div>

            {onVisualization && (
              <div className="group cursor-pointer" onClick={onVisualization}>
                <div className="p-4 md:p-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all duration-200 group-hover:shadow-md active:bg-purple-100">
                  <div className="text-center">
                    <div className="mx-auto h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
                      <svg style={{width: '24px', height: '24px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Network Visualization</h3>
                    <p className="text-sm text-gray-600 mb-4">Interactive network maps and relationship analysis</p>
                    <div className="flex items-center justify-center text-xs text-gray-500">
                      <span className="bg-gray-100 px-2 py-1 rounded-full">D3.js • Interactive</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage